// The event-type registry and the event payloads, shared by server and SPA.
//
// Port of app/core/functions/event_types.php.
//
// An event TYPE is a behavior profile, not a label. The registry lives HERE, in
// code — this is deliberately NOT a CMS. Adding a type is a code edit. Group
// organizers customize only two things: the message copy (`typeMessages`) and
// type-specific values (`typeConfig`). They cannot define types or fields.
//
// It lives in `shared/` rather than on the server because the SPA needs the same
// facts the server enforces: which types exist, which fields each one makes
// meaningful, and what the DEFAULT copy for a hook is (so the override form can
// show it as placeholder text). Two copies of that table would drift.
import { eventInstant, siteDateAt } from './time.js';
export const CLOSE_REASONS = ['completed', 'canceled'];
export const EVENT_FAMILIES = {
    game: {
        key: 'game',
        label: 'Games',
        icon: '🎲',
        hint: 'Track who played and who won. Pick each game this group plays.',
    },
    health: {
        key: 'health',
        label: 'Health',
        icon: '💪',
        hint: 'Fasts, challenges and workouts. Pick each one this group does.',
    },
    faith: {
        key: 'faith',
        label: 'Faith',
        icon: '⛪',
        hint: 'Studies, services and gatherings. Pick what this group meets for.',
    },
};
/** Render order for the picker's headings. */
export const EVENT_FAMILY_KEYS = ['game', 'health', 'faith'];
/**
 * The full registry. Add new types here — nowhere else.
 *
 * The flags are carried over from the PHP original and must not be "tidied":
 * chatVisibility differing between a game (yes_only — only the people actually
 * playing belong in the game thread) and a health type (yes_maybe — people
 * still deciding get the encouragement too) is a deliberate product decision,
 * not an inconsistency.
 *
 * SCAFFOLDING NOTE, 2026-09-09. `chess`, `mahjong` and `workout` are new leaves
 * whose per-activity behavior is NOT built yet: they currently inherit their
 * family's flags wholesale, so a chess result is recorded exactly like a poker
 * result (winners in placement order, `isChop` for a split). That is wrong in a
 * way that matters — a chess DRAW is not a chop, and mahjong wants per-player
 * scores rather than an ordering. The labels and copy are real; the mechanics of
 * each one land one at a time. Do not read the shared flags as a decision.
 */
export const EVENT_TYPES = {
    generic: {
        key: 'generic',
        family: null,
        label: 'General event',
        icon: '📅',
        hooks: {}, // no automated lifecycle messages
        chatVisibility: 'yes_maybe',
        closeMode: 'manual',
        hasResults: false,
        usesEndDate: false,
        participatedViaFasting: false,
    },
    // --- Games ---------------------------------------------------------------
    poker: {
        key: 'poker',
        family: 'game',
        label: 'Poker',
        icon: '🃏',
        hooks: {
            needs_min: '🃏 {count} of {min} in so far for {title}. Who else is coming?',
            min_met: '🃏 Game on! We hit {min} for {title}. See everyone there.',
            full: "🪑 Table's full: {count}/{max} locked in for {title}!",
            ended: "🏁 That's a wrap on {title}. Congrats {winners}! 🏆",
        },
        chatVisibility: 'yes_only',
        closeMode: 'manual', // poker runs till it runs
        hasResults: true, // close prompts for winners; chops allowed
        usesEndDate: false,
        participatedViaFasting: false,
    },
    chess: {
        key: 'chess',
        family: 'game',
        label: 'Chess',
        icon: '♟️',
        hooks: {
            needs_min: '♟️ Waiting on an opponent for {title}: {count} of {min} so far.',
            min_met: '♟️ Both players are in for {title}. Good luck.',
            full: '♟️ {title} is set: {count}/{max} confirmed.',
            ended: '♟️ {title} is done. Congrats {winners}! 🏆',
        },
        chatVisibility: 'yes_only',
        closeMode: 'manual',
        // Placeholder mechanics: a draw currently has to be entered as a two-winner
        // chop, which reads wrong on the card. Chess results are their own job.
        hasResults: true,
        usesEndDate: false,
        participatedViaFasting: false,
    },
    mahjong: {
        key: 'mahjong',
        family: 'game',
        label: 'Mahjong',
        icon: '🀄',
        hooks: {
            needs_min: '🀄 Need {min} for a table: {count} in so far for {title}.',
            min_met: '🀄 We have a table for {title}. See everyone there.',
            full: '🀄 Table full: {count}/{max} in for {title}!',
            ended: '🀄 {title} is over. Congrats {winners}! 🏆',
        },
        chatVisibility: 'yes_only',
        closeMode: 'manual',
        // Placeholder mechanics: mahjong wants a score per player, not a placement
        // order. Recorded as an ordering until that is built.
        hasResults: true,
        usesEndDate: false,
        participatedViaFasting: false,
    },
    // --- Health --------------------------------------------------------------
    fasting: {
        key: 'fasting',
        family: 'health',
        // ⚠ "the group aims at" is the whole model. A fasting EVENT is a
        // SUGGESTION, not a window everybody occupies: people start the night
        // before, at dawn, whenever -- and each person's real clock lives in
        // `fast_sessions`, per user, with its own started_at and ended_at.
        label: 'Fast (a date the group aims at)',
        icon: '⏳',
        hooks: {
            ended: '🎉 {title} is complete. Nice work, everyone!',
        },
        chatVisibility: 'yes_maybe',
        // ⚠ WAS 'auto_at_end' UNTIL 2026-09-12, and it had to change with
        // usesEndDate below -- cron's Stage 3 closes on `end_date`, which is now
        // always NULL for this type, so leaving it would have meant a fast that
        // never closes and never fires its `ended` hook, with nothing erroring.
        // `eventTypesAutoClose()` is now empty and Stage 3 logs "no auto-closing
        // types", which is honest rather than silent.
        closeMode: 'manual',
        hasResults: false,
        // ⚠ FALSE as of 2026-09-12. It was the only type with an end date, and
        // asking for one was a real inconsistency rather than a preference: the
        // per-person truth already lives in `fast_sessions` (a GROUP row with a
        // NULLABLE event_id, chosen precisely so a fast need not belong to an
        // event), and the fasting screen already tells members "the event is the
        // target, and your own clock starts when you say it does". `events.end_date`
        // was a second, group-wide answer to a question that already had a
        // per-user one, and the two could disagree.
        //
        // The "rough suggested start time" Will asked for needs NO new column:
        // `event_date` + `start_time` already are it.
        //
        // ⚠ A NEW CAPABILITY RIDES ALONG, flagged rather than left to be found.
        // `resolveTypeFields()` refuses recurrence for a usesEndDate type, so
        // flipping this makes a RECURRING fast expressible through the series
        // endpoint for the first time -- a monthly group fast now creates. That is
        // plausibly wanted and was never decided, so it is called out here.
        usesEndDate: false,
        // ⚠ THE ONLY TRUE IN THE REGISTRY, and the reason the flag exists. The
        // event is a date the group aims at; the fast itself is started, ticked and
        // ended on the group's fasting page, so the event needs a door to it.
        participatedViaFasting: true,
    },
    // --- Faith ---------------------------------------------------------------
    /**
     * A study, a service, a men's breakfast -- anything a faith group meets for.
     *
     * ⚠ ITS BEHAVIOUR IS IDENTICAL TO `generic` TODAY, DELIBERATELY. Will,
     * 2026-09-10, describing how his Tuesday morning study actually runs: *"these
     * events are basically general events. They're not anything special. But as I
     * get these men into this group and we talk about it, we may come up with
     * special things we wanna add into here."*
     *
     * So this leaf exists to be an EXTENSION POINT, not because it behaves
     * differently yet. Adding the key now rather than later is what avoids a
     * key-rename migration when it does gain behaviour: `event_type` is a
     * varchar, so new events store `gathering` from today and any future hook,
     * result or close rule applies to rows that already exist.
     *
     * ⚠ That is not a theoretical saving. The `threshold`→`poker` rename in
     * CLAUDE.md is the same migration done late, and it failed SILENTLY -- the
     * pre-split build validated stored keys strictly, so every migrated row read
     * as `generic`, lifecycle announcements stopped, the close dialog stopped
     * prompting, and cron stopped auto-closing. Nothing errored and nothing
     * logged.
     *
     * ⚠ ONE leaf covers the study AND the pancake breakfast on purpose. They are
     * mechanically identical -- one session, RSVP for headcount, open chat,
     * manual close, nobody wins -- and two registry entries with the same
     * behaviour is two picker options that do the same thing. The event TITLE
     * distinguishes them. Split it only when their BEHAVIOUR diverges.
     *
     * Note what this type is actually for, from the same conversation: the RSVP
     * count is the point. The organizer needs to know how many tables and chairs
     * to set out, which is also why day-of confirmation fits it well.
     */
    gathering: {
        key: 'gathering',
        family: 'faith',
        label: 'Gathering (study, breakfast, home group)',
        icon: '🙏',
        // Empty, matching `generic`. The first override somebody asks for is the
        // signal that this leaf has earned behaviour of its own.
        hooks: {},
        // A person still deciding whether to come is exactly who the thread should
        // reach, so this follows generic and workout rather than the games.
        chatVisibility: 'yes_maybe',
        closeMode: 'manual',
        hasResults: false, // nobody wins a Bible study
        usesEndDate: false, // a morning, not a course. A multi-week study would be
        // its own leaf with usesEndDate true, exactly as the workout note says.
        participatedViaFasting: false,
    },
    workout: {
        key: 'workout',
        family: 'health',
        label: 'Workout (a single session)',
        icon: '💪',
        hooks: {
            needs_min: '💪 {count} of {min} so far for {title}. Bring a friend!',
            min_met: '💪 We have {min} for {title}. See everyone there.',
            ended: '💪 {title} is done. Nice work, everyone!',
        },
        // A session someone is still deciding about is exactly who the thread should
        // encourage, so this follows fasting rather than the games.
        chatVisibility: 'yes_maybe',
        closeMode: 'manual',
        hasResults: false, // nobody "wins" a workout
        usesEndDate: false, // a session, not a challenge. A multi-day
        // challenge would be its own leaf with usesEndDate true, not this one.
        // ⚠ FALSE despite family 'health'. A workout is not taken part in on the
        // fasting page, which is exactly why that flag could not be the family.
        participatedViaFasting: false,
    },
};
/** Registry order. Drives every picker and the /event-types payload. */
export const EVENT_TYPE_KEYS = [
    'generic',
    'poker',
    'chess',
    'mahjong',
    'fasting',
    'workout',
    'gathering',
];
/**
 * Keys that USED to be stored, mapped to the leaf that replaced them.
 *
 * `threshold` and `duration` named a behavior rather than an activity, so each
 * could only ever be one game and one health type. Splitting them into leaves
 * renamed the stored value, and rows written before the rename still hold the
 * old string — so this map exists on the READ path only.
 *
 * Two rules, both load-bearing:
 *  • `normalizeEventTypeKey()` resolves these, so an un-migrated row keeps its
 *    real behavior. Without it a stored `threshold` fails `eventTypeExists()`
 *    and reads as `generic`, which SILENTLY drops yes-only chat and results on
 *    every existing poker game.
 *  • `eventTypeExists()` does NOT resolve them, so a client cannot create new
 *    rows carrying a retired key. Requests are validated strictly; only the
 *    database is read leniently.
 *
 * Delete this map only once no row in any environment holds an old key.
 */
export const LEGACY_EVENT_TYPE_ALIASES = {
    threshold: 'poker',
    duration: 'fasting',
};
/**
 * Types every group may always create, whatever `allowed_event_types` says.
 *
 * A plain event is the FLOOR, not a feature a group opts into: any group can
 * always just put something on the calendar. So `generic` is not offered in the
 * picker, is never written to `allowed_event_types`, and — load-bearing — must
 * be permitted by the server even for a group whose allow-list omits it.
 * Enforce via `isEventTypeAllowed()`; a bare `allowed.includes(key)` refuses
 * plain events for every group that narrowed its list.
 */
export const ALWAYS_ALLOWED_EVENT_TYPES = ['generic'];
/** Types the group-settings picker offers, i.e. everything with a family. */
export const SELECTABLE_EVENT_TYPE_KEYS = EVENT_TYPE_KEYS.filter((k) => EVENT_TYPES[k].family !== null);
/** The selectable types under one family heading, in registry order. */
export function eventTypesInFamily(family) {
    return EVENT_TYPE_KEYS.filter((k) => EVENT_TYPES[k].family === family);
}
/**
 * May this group create an event of this type?
 *
 * `allowed` is the group's `allowed_event_types`, where NULL means open. The
 * always-allowed set wins over a narrowed list; see ALWAYS_ALLOWED_EVENT_TYPES.
 */
export function isEventTypeAllowed(key, allowed) {
    if (ALWAYS_ALLOWED_EVENT_TYPES.includes(key))
        return true;
    if (allowed === null)
        return true;
    return allowed.includes(key);
}
/**
 * Neutral copy for a canceled close. Deliberately not in the registry's `hooks`
 * map: cancellation is not a per-type behavior, it is the one message that must
 * never be the celebratory `ended` copy. See `closeMessage()`.
 */
export const CANCELED_MESSAGE = '🚫 {title} has been canceled.';
// --- Registry lookups -----------------------------------------------------
/**
 * Is `key` a real registered type?
 *
 * STRICT: a retired key (see LEGACY_EVENT_TYPE_ALIASES) is not a real type and
 * fails here on purpose, so nothing new is ever written carrying one. Use
 * `normalizeEventTypeKey()` for values coming OUT of the database.
 */
export function eventTypeExists(key) {
    return Object.prototype.hasOwnProperty.call(EVENT_TYPES, key);
}
/**
 * A stored `event_type` resolved to a live registry key: retired keys are
 * mapped forward, anything unrecognized reads as `generic`.
 *
 * Every DB read path uses this. Request validation uses `eventTypeExists()`.
 */
export function normalizeEventTypeKey(raw) {
    if (eventTypeExists(raw))
        return raw;
    return LEGACY_EVENT_TYPE_ALIASES[raw] ?? 'generic';
}
/**
 * A type definition, resolving retired keys and falling back to `generic` for
 * anything unknown. Every other lookup below goes through this, so they all
 * inherit the alias resolution.
 */
export function eventType(key) {
    return EVENT_TYPES[normalizeEventTypeKey(key)];
}
/**
 * Does this type span days — end_date meaningful, countdown shown, recurrence
 * refused?
 *
 * The one question five different rules were each answering with a literal
 * `=== 'duration'`. Ask it here instead: a new multi-day leaf then works
 * everywhere without touching the server, the event form or the series form.
 */
export function eventTypeUsesEndDate(key) {
    return eventType(key).usesEndDate;
}
/**
 * Is this event taken part in on the group's fasting page?
 *
 * The event page has no fasting affordance without this: a member who opens an
 * announced fast finds Chat and Group feed and no path at all to the thing the
 * event is asking them to do. "They are not going to know to go to the fasting
 * tab" -- and they were right.
 */
export function eventTypeParticipatedViaFasting(key) {
    return eventType(key).participatedViaFasting;
}
/** Does this type react to the given lifecycle hook? */
export function eventTypeHasHook(key, hook) {
    return eventType(key).hooks[hook] !== undefined;
}
/**
 * Can this hook ever fire on an event configured this way?
 *
 * The RSVP hooks are gated on a THRESHOLD as well as on the type. `min_met`
 * needs an `rsvp_min` and `full` needs an `rsvp_max`; the server's
 * `fireRsvpHooks()` skips on `if (threshold === null) continue`, so with the box
 * blank there is no count that can ever cross it. `ended` has no threshold and
 * fires on close.
 *
 * This lives here rather than in the form because it is a RULE, not a rendering
 * decision, and `web/` has no test runner — the same reason `tzPool` and the
 * event registry sit in this package. It exists because both event and series
 * forms offered an editable "When the minimum is reached" message on events with
 * no minimum: configuration that looked live, saved fine, and could never be
 * sent. Keep it in step with `fireRsvpHooks()`; if the server ever gains a
 * default threshold, this is the other half.
 */
export function hookCanFire(key, hook, thresholds) {
    if (!eventTypeHasHook(key, hook))
        return false;
    if (hook === 'min_met' || hook === 'needs_min')
        return thresholds.rsvpMin !== null;
    if (hook === 'full')
        return thresholds.rsvpMax !== null;
    return true;
}
export function eventTypeCloseMode(key) {
    return eventType(key).closeMode;
}
/** Does closing this type prompt for winners? */
export function eventTypeHasResults(key) {
    return eventType(key).hasResults;
}
/**
 * Stored `event_type` values whose events auto-close at their end datetime
 * (cron, Phase 9). Goes straight into a SQL `IN (...)`.
 *
 * RETIRED KEYS ARE INCLUDED, which is why this returns strings rather than
 * EventTypeKey. An un-migrated row still holds `duration`, and cron matches the
 * column literally — leave the old key out and every existing fast simply never
 * closes, with nothing failing and nothing logged.
 */
export function eventTypesAutoClose() {
    const live = EVENT_TYPE_KEYS.filter((k) => EVENT_TYPES[k].closeMode === 'auto_at_end');
    const retired = Object.keys(LEGACY_EVENT_TYPE_ALIASES).filter((old) => live.includes(LEGACY_EVENT_TYPE_ALIASES[old]));
    return [...live, ...retired];
}
/** Which RSVP statuses admit a member to this type's event chat (Phase 8). */
export function eventTypeChatStatuses(key) {
    return eventType(key).chatVisibility === 'yes_only' ? ['yes'] : ['yes', 'maybe'];
}
function substitute(template, vars) {
    let out = template;
    for (const [key, value] of Object.entries(vars)) {
        if (value === undefined || value === null)
            continue;
        out = out.split(`{${key}}`).join(String(value));
    }
    return out;
}
/**
 * Resolve the copy for a type's lifecycle hook: the organizer's override when
 * non-empty, else the registry default, with {placeholders} substituted.
 *
 * Returns null when the type has no such hook — meaning nothing should fire.
 * That null is the whole control mechanism, so callers must not coerce it to a
 * default string.
 */
export function eventTypeMessage(key, hook, overrides, vars = {}) {
    const fallback = eventType(key).hooks[hook];
    if (fallback === undefined)
        return null;
    const override = (overrides?.[hook] ?? '').trim();
    return substitute(override !== '' ? override : fallback, vars);
}
/**
 * The message a close should post, or null for "post nothing".
 *
 * Three rules live here together because they only make sense together:
 *   • A `canceled` close NEVER posts the celebratory copy. It posts the neutral
 *     cancellation notice instead, gated on the same "does this type announce
 *     at all" check, so a Generic event stays as silent on cancel as on close.
 *   • For a `has_results` type the ended post IS the results announcement, so it
 *     fires only when winners were entered. Closing poker with nobody named
 *     posts nothing.
 *   • Everything else resolves the `ended` copy normally.
 */
export function closeMessage(key, reason, overrides, vars, winnerCount) {
    if (!eventTypeHasHook(key, 'ended'))
        return null;
    if (reason === 'canceled')
        return substitute(CANCELED_MESSAGE, vars);
    if (eventTypeHasResults(key) && winnerCount === 0)
        return null;
    return eventTypeMessage(key, 'ended', overrides, vars);
}
/**
 * Reduce posted copy overrides to the hooks this type actually has, trimmed and
 * non-empty. Unknown keys are discarded rather than stored, so a client cannot
 * smuggle arbitrary JSON into the column.
 *
 * Returns null when nothing survives, which is the "store NULL" signal.
 */
export function sanitizeTypeMessages(key, posted) {
    if (!posted)
        return null;
    const clean = {};
    let any = false;
    for (const hook of Object.keys(eventType(key).hooks)) {
        const raw = posted[hook];
        if (typeof raw !== 'string')
            continue;
        const value = raw.trim();
        if (value === '')
            continue;
        clean[hook] = value;
        any = true;
    }
    return any ? clean : null;
}
/**
 * Render winner names the way the `ended` copy reads best: "A, B & C", with a
 * "(chop)" tag when the pot was split. Shared so the SPA can preview the exact
 * string the server will post.
 */
export function winnersLabel(names, isChop) {
    if (names.length === 0)
        return '';
    if (names.length === 1)
        return names[0];
    const head = names.slice(0, -1).join(', ');
    return `${head} & ${names[names.length - 1]}${isChop ? ' (chop)' : ''}`;
}
/** An even split of the pot across `n` players, odd cents to the first listed. */
export function splitEven(potCents, n) {
    if (n <= 0)
        return [];
    const base = Math.floor(potCents / n);
    const rem = potCents - base * n;
    return Array.from({ length: n }, (_, k) => base + (k < rem ? 1 : 0));
}
/**
 * Divide a pot across places, then across the players tied in each place.
 *
 * Integer cents throughout, so the payouts always add up to the pot. Percent
 * is held as basis points to avoid float drift. The pot is conserved: when the
 * percentages total exactly 100, whatever rounding leaves over goes to first
 * place (the same person who would be ahead in a dispute), and within a chop
 * the odd cents go one each to the first players listed. When they total less
 * than 100 the rest is simply not paid out.
 *
 * Shared so the close form can show each player's amount live and the server
 * stores exactly the same numbers.
 */
export function splitPot(potCents, places) {
    const bps = places.map((p) => Math.round(p.pct * 100));
    const shares = bps.map((bp) => Math.floor((potCents * bp) / 10000));
    if (bps.reduce((a, b) => a + b, 0) === 10000 && shares.length > 0) {
        shares[0] = (shares[0] ?? 0) + (potCents - shares.reduce((a, b) => a + b, 0));
    }
    return places.map((p, i) => {
        const share = shares[i] ?? 0;
        if (p.count <= 0)
            return [];
        const base = Math.floor(share / p.count);
        const rem = share - base * p.count;
        return Array.from({ length: p.count }, (_, k) => base + (k < rem ? 1 : 0));
    });
}
// --- When does an event end? ----------------------------------------------
/**
 * The event's end as a real INSTANT, resolved in its authoring zone.
 *
 * One function so the server's "too late to confirm" check and the client's
 * "should I still offer the confirm button" check can never disagree. A
 * disagreement here shows up as a button that always errors when tapped.
 *
 * The rule, matching durationBounds() in the PHP original:
 *   end_date set  -> end_date  + (end_time or 23:59:59)   [Duration, multi-day]
 *   end_date null -> event_date + (end_time or 23:59:59)
 *
 * Note the fallback is 23:59:59, not the start time: an event with no stated end
 * runs until the end of its day. Treating a missing end as "ends immediately"
 * would lock members out of confirming an event that is currently happening.
 */
export function eventEndsAt(eventDate, endDate, endTime, authoringZone) {
    const lastDay = endDate || eventDate;
    if (!lastDay)
        return null;
    return eventInstant(lastDay, endTime || '23:59:59', authoringZone);
}
/**
 * Is the event happening TODAY, in its own authoring zone?
 *
 * The predicate behind "an RSVP made on the day is itself a day-of
 * confirmation". Both sides need it and must agree: the server decides whether
 * to stamp `confirmed_dayof`, the client decides whether to show the "still
 * coming?" prompt at all. Two copies would show a prompt the server had already
 * answered, or hide one it had not.
 *
 * `authoringZone` is the RESOLVED zone (event -> group -> site), which is what
 * `EventSummary.timeZone` already carries. Comparing naive date strings is
 * correct here and a UTC comparison is not: at 21:00 in Denver it is already
 * tomorrow in UTC, so a bare `CURDATE()` would call an event happening tonight
 * "not today" for the last three hours of its own day.
 *
 * All-day events (`start_time` NULL) need no special case — the whole day
 * matches, which is exactly right.
 */
export function isEventDayNow(eventDate, authoringZone, nowMs) {
    if (!eventDate)
        return false;
    return eventDate === siteDateAt(authoringZone, nowMs);
}
/** True when the event's end instant is in the past. `nowMs` is injectable for tests. */
export function eventHasEnded(eventDate, endDate, endTime, authoringZone, nowMs) {
    const end = eventEndsAt(eventDate, endDate, endTime, authoringZone);
    return end !== null && end.toMillis() < nowMs;
}
// --- Duration helpers -----------------------------------------------------
/**
 * Compact human duration: "2d 4h", "45m", "<1m". Minutes are shown only under a
 * day, because "3d 2h 17m" is noise on a multi-day fast.
 */
export function humanDuration(seconds) {
    let secs = seconds < 0 ? 0 : Math.floor(seconds);
    const d = Math.floor(secs / 86400);
    secs %= 86400;
    const h = Math.floor(secs / 3600);
    secs %= 3600;
    const m = Math.floor(secs / 60);
    const parts = [];
    if (d)
        parts.push(`${d}d`);
    if (h)
        parts.push(`${h}h`);
    if (!d && m)
        parts.push(`${m}m`);
    if (!parts.length)
        parts.push('<1m');
    return parts.join(' ');
}
/** Countdown text for a Duration event. Empty string when there is no end. */
export function durationCountdownText(startMs, endMs, nowMs) {
    if (endMs !== null && nowMs >= endMs)
        return 'Complete';
    if (startMs !== null && nowMs < startMs)
        return `Starts in ${humanDuration((startMs - nowMs) / 1000)}`;
    if (endMs !== null)
        return `${humanDuration((endMs - nowMs) / 1000)} left`;
    return '';
}
// --- The "next up" panel: membership, ordering and selection --------------
//
// These are RULES, not rendering, so they live here rather than in
// `web/src/views/stickyPanel.ts`: `web/` has no test runner, and the ordering
// below has a tiebreak that is easy to get subtly wrong and impossible to
// assert on from an API smoke test. Same reasoning as `hookCanFire()`.
//
// Designed 2026-09-10, docs/plan-next-up.md.
/**
 * Has the viewer answered this event at all?
 *
 * "Unanswered" is exactly `myStatus === null`. **`maybe` COUNTS AS ANSWERED** —
 * Will's call, 2026-09-10. A maybe is load-bearing (`yes_maybe` event types
 * admit a maybe into the chat), so treating it as an open question would nag
 * people who answered honestly.
 *
 * Note this is deliberately independent of `myConfirmedDayof`. An advance yes
 * awaiting its day-of confirmation is ANSWERED for the purposes of this panel;
 * the day-of prompt is `rsvpControl()`'s job, not the backlog's.
 */
export function isEventUnanswered(event) {
    return event.myStatus === null;
}
/** How many of these need an answer. Drives the count in the panel head. */
export function unansweredCount(events) {
    return events.reduce((n, e) => n + (isEventUnanswered(e) ? 1 : 0), 0);
}
/**
 * The events worth offering in the "Next up" panel, ordered.
 *
 * **Membership: every open, not-yet-ended event. No date horizon, and none is
 * needed.** A recurring series does not stack up ninety occurrences —
 * `materializeDueOccurrences()` creates one only once its trigger instant has
 * passed, and the trigger is `date` minus `feed_post_days_before`. So the events
 * that exist are already near by construction, and a horizon would only be a
 * second way to lose the three-month-out invite this ordering exists to surface.
 *
 * **Ordering: UNANSWERED first, then going/maybe, then DECLINED last; soonest
 * within each tier.** Sorted purely
 * by date, "what needs my answer" loses every time — a far-off invite you have
 * never replied to never reaches the list, not because it is far away but
 * because it is sixth. An answered event STAYS in the list, below the
 * unanswered ones: you may change your mind, and the panel is where you would
 * do it.
 *
 * `limit` is optional and unset by default — the old hard 5 was half of the
 * defect. Pass one only where a surface genuinely cannot grow.
 */
export function orderPinnableEvents(events, nowMs, limit) {
    const open = events.filter((e) => e.closedAt === null &&
        !eventHasEnded(e.eventDate, e.endDate, e.endTime, e.timeZone, nowMs));
    const sorted = open.slice().sort((a, b) => {
        const ta = answerTier(a);
        const tb = answerTier(b);
        if (ta !== tb)
            return ta - tb;
        return sortKey(a).localeCompare(sortKey(b));
    });
    return limit === undefined ? sorted : sorted.slice(0, limit);
}
/**
 * 0 unanswered, 1 going or maybe, 2 declined. A "no" sinks to the bottom: it is
 * still listed (you may change your mind) but must never sit above, or be
 * selected over, an event that still wants something from the viewer. Will's
 * call, 2026-10-16: a declined run hid the poker night under it.
 */
function answerTier(event) {
    if (isEventUnanswered(event))
        return 0;
    return event.myStatus === 'no' ? 2 : 1;
}
/**
 * The instant to sort on. `startsAtUtc` where the server could compute it, else
 * the naive date — which is why this is a string compare and not a number: both
 * forms are lexicographically ordered, and an all-day event has no instant.
 */
function sortKey(event) {
    return event.startsAtUtc ?? event.eventDate;
}
/**
 * Which event the panel opens on: **today's event, else the soonest unanswered,
 * else the soonest.**
 *
 * This is what protects day-of confirmation while the list widens. Membership
 * and default selection are separate decisions, and that separation is the whole
 * idea: the list can grow a long way without ever costing job one, as long as
 * the selection still lands on today's event when there is one.
 *
 * `events` must already be through `orderPinnableEvents()`, so "the soonest" is
 * a scan for the first match rather than another sort. Returns 0 for an empty
 * list, which the caller treats as nothing to render.
 *
 * "Today" resolves in the event's AUTHORING zone via `isEventDayNow()`. A bare
 * UTC date calls tonight's event "not today" for the last three hours of its
 * own day.
 */
export function defaultPinnedIndex(events, nowMs) {
    if (events.length === 0)
        return 0;
    // Today's, and the EARLIEST of them if a day holds several — the list is
    // ordered unanswered-first, so the first today row is not necessarily the
    // soonest one.
    let today = -1;
    for (let i = 0; i < events.length; i += 1) {
        const e = events[i];
        if (!e || !isEventDayNow(e.eventDate, e.timeZone, nowMs))
            continue;
        // A declined event has nothing to confirm, so it must not claim the default.
        if (e.myStatus === 'no')
            continue;
        const best = today === -1 ? undefined : events[today];
        if (!best || sortKey(e).localeCompare(sortKey(best)) < 0)
            today = i;
    }
    if (today !== -1)
        return today;
    const unanswered = events.findIndex((e) => isEventUnanswered(e));
    return unanswered === -1 ? 0 : unanswered;
}
/** Events happening TODAY in their own authoring zone, open and not ended. */
export function dayOfEvents(events, nowMs) {
    return orderPinnableEvents(events, nowMs).filter((e) => isEventDayNow(e.eventDate, e.timeZone, nowMs));
}
/**
 * The complement of `dayOfEvents()` — everything NOT on today.
 *
 * Feed mode renders the day-of box AND a card per event, and until 2026-09-10
 * today's event was in both. That was deliberate and documented (§3e: the box
 * is the confirmation surface and must not be scrollable-past) but it read as a
 * bug to the person looking at it, which is the only test that matters for a
 * duplicate: one event, two RSVP controls, two headcounts, and no way to tell
 * they were the same row. Will's call — the box keeps the event, the feed does
 * not repeat it.
 *
 * It is defined HERE, beside `dayOfEvents()` and over the same predicate,
 * rather than as a `.filter()` at the call site, because the two lists are only
 * safe if they PARTITION: an event in both is the duplicate this removes, and
 * an event in neither vanishes from the page entirely. A test asserts exactly
 * that, so the predicate cannot drift on one side.
 *
 * Both take `nowMs` for the same reason: one tick, one clock. "Today" resolves
 * in the event's AUTHORING zone, so two calls straddling a midnight boundary
 * with two different instants can disagree about the same event.
 */
export function nonDayOfEvents(events, nowMs) {
    return orderPinnableEvents(events, nowMs).filter((e) => !isEventDayNow(e.eventDate, e.timeZone, nowMs));
}
/** When cron stage 2 sends the organizer's headcount, unless the event sets its own. */
export const HEADCOUNT_LEAD_DEFAULT_MINUTES = 60;
/**
 * The bounds of the per-event setting. The day-of ping goes out 4 hours ahead, so the
 * headcount must not be earlier than that or it would precede the confirmations.
 */
export const HEADCOUNT_LEAD_MIN_MINUTES = 15;
export const HEADCOUNT_LEAD_MAX_MINUTES = 240;
//# sourceMappingURL=events.js.map